import React, { useState, useEffect } from 'react';
import Input from '../Common/Input';
import Button from '../Common/Button';
import { CALENDAR_COLORS } from '../../services/calendarStorage';
import { logGoalCreated, logGoalEdited } from '../../services/analytics';

const GoalForm = ({ goal, onSave, onCancel }) => {
    const currentYear = new Date().getFullYear();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        period: 'monthly',
        year: currentYear,
        quarter: 1,
        month: new Date().getMonth() + 1,
        color: 'blue'
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (goal) {
            setFormData({
                title: goal.title || '',
                description: goal.description || '',
                period: goal.period || 'monthly',
                year: goal.year || currentYear,
                quarter: goal.quarter || 1,
                month: goal.month || new Date().getMonth() + 1,
                color: goal.color || 'blue'
            });
        }
    }, [goal, currentYear]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.period) {
            newErrors.period = 'Period is required';
        }

        if (!formData.year || formData.year < 2000 || formData.year > 2100) {
            newErrors.year = 'Please enter a valid year';
        }

        if (formData.period === 'quarterly') {
            if (!formData.quarter || formData.quarter < 1 || formData.quarter > 4) {
                newErrors.quarter = 'Quarter must be between 1 and 4';
            }
        }

        if (formData.period === 'monthly') {
            if (!formData.month || formData.month < 1 || formData.month > 12) {
                newErrors.month = 'Month must be between 1 and 12';
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const goalData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                period: formData.period,
                year: parseInt(formData.year),
                color: formData.color
            };

            // Only include quarter/month if relevant
            if (formData.period === 'quarterly') {
                goalData.quarter = parseInt(formData.quarter);
            }
            if (formData.period === 'monthly') {
                goalData.month = parseInt(formData.month);
            }

            await onSave(goalData);
            if (goal) {
                logGoalEdited({ period: formData.period });
            } else {
                logGoalCreated({ period: formData.period, year: parseInt(formData.year) });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrors({ submit: error.message || 'Failed to save goal. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <Input
                label="Goal Title"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter goal title"
                required
                error={errors.title}
            />

            <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description (Optional)
                </label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter goal description"
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
            </div>

            <div className="mb-4">
                <label htmlFor="period" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Period
                </label>
                <select
                    id="period"
                    name="period"
                    value={formData.period}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={goal} // Don't allow changing period for existing goals
                >
                    <option value="yearly">Yearly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                </select>
                {errors.period && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.period}</p>
                )}
            </div>

            <Input
                label="Year"
                id="year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleChange}
                min="2000"
                max="2100"
                required
                error={errors.year}
                disabled={goal} // Don't allow changing year for existing goals
            />

            {formData.period === 'quarterly' && (
                <div className="mb-4">
                    <label htmlFor="quarter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Quarter
                    </label>
                    <select
                        id="quarter"
                        name="quarter"
                        value={formData.quarter}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        disabled={goal} // Don't allow changing quarter for existing goals
                    >
                        <option value="1">Q1 (Jan - Mar)</option>
                        <option value="2">Q2 (Apr - Jun)</option>
                        <option value="3">Q3 (Jul - Sep)</option>
                        <option value="4">Q4 (Oct - Dec)</option>
                    </select>
                    {errors.quarter && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.quarter}</p>
                    )}
                </div>
            )}

            {formData.period === 'monthly' && (
                <div className="mb-4">
                    <label htmlFor="month" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Month
                    </label>
                    <select
                        id="month"
                        name="month"
                        value={formData.month}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        disabled={goal} // Don't allow changing month for existing goals
                    >
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                    </select>
                    {errors.month && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.month}</p>
                    )}
                </div>
            )}

            <div className="mb-4">
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                </label>
                <select
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                    {CALENDAR_COLORS.map(color => (
                        <option key={color.value} value={color.value}>
                            {color.name}
                        </option>
                    ))}
                </select>
            </div>

            {errors.submit && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
                </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : goal ? 'Update Goal' : 'Create Goal'}
                </Button>
            </div>
        </form>
    );
};

export default GoalForm;
